/**
 * Admission Coordination Dialog
 *
 * Main dialog for coordinating admission visits.
 * Step 1: Coordinate admission visit (supplies, transport, DME, provider selection).
 * Step 2: Review care pattern and confirm auto-scheduling for the full episode.
 */

import { useState, useEffect } from 'react';
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid2,
  IconButton,
  Divider,
  Paper,
  Tab,
  Tabs,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { addHours, addMinutes, format, isSameDay, startOfDay } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { gql, useQuery } from '@apollo/client';
import { VelocareIntegrationPanel } from './VelocareIntegrationPanel';
import { ManualCoordinationChecklist, ManualCoordinationStatus } from './ManualCoordinationChecklist';
import { AdmissionVisitOptionsPanel, SelectedAdmissionOption } from './AdmissionVisitOptionsPanel';
import { TodaysVisitScheduler } from './TodaysVisitScheduler';
import { FutureVisitCoordinationStep } from './FutureVisitCoordinationStep';
import {
  getAdmissionCoordination,
  saveAdmissionCoordination,
  AdmissionCoordinationState,
} from '~/services/demo/demoStateManager';
import { CARE_PATTERNS } from '~/services/demo/mockAutoSchedulingData';
import { VisitType, useOrgConfig } from '~/providers/OrgConfigProvider';
import { timeLabelWithZone } from '@caredna-marlin/utils/dates';

export interface AdmissionCoordinationDialogProps {
  open: boolean;
  onClose: () => void;
  onComplete: (data: {
    admissionTime: string;
    providerResources: Array<{ providerId: number }>;
    autoScheduleToday: boolean;
    selectedVisitTypeId: number | null;
  }) => Promise<void>;
  isSimulationMode: boolean;
  admissionVisitType: VisitType | null;
}

interface VelocareStatus {
  supplies: {
    status: 'pending' | 'confirmed' | 'in_transit' | 'delivered';
    estimatedDelivery: string | null;
    items: string[];
  };
  rpm: {
    status: 'pending' | 'confirmed' | 'scheduled' | 'installed';
    estimatedArrival: string | null;
  };
}

const PATIENT_TIMEZONE_QUERY = gql`
  query PatientTimezone($patientId: Int!)
  @permission(requires: useWebApp)
  {
    patientSingle(where: { id: { eq: $patientId } }) {
      id
      addressTimezone
    }
  }
`;


export function AdmissionCoordinationDialog({
  open,
  onClose,
  onComplete,
  isSimulationMode,
  admissionVisitType,
}: AdmissionCoordinationDialogProps) {
  const orgConfig = useOrgConfig();
  const [activeStep, setActiveStep] = useState(0);
  const [coordination, setCoordination] = useState<AdmissionCoordinationState | null>(null);

  // Fetch patient timezone
  const { data: patientData } = useQuery(PATIENT_TIMEZONE_QUERY, {
    variables: { patientId: coordination?.patientId || 0 },
    skip: !coordination?.patientId,
  });
  const patientTimezone = patientData?.patientSingle?.addressTimezone || 'America/New_York';

  const [velocareStatus, setVelocareStatus] = useState<VelocareStatus | null>(null);
  const [manualCoordination, setManualCoordination] = useState<ManualCoordinationStatus | null>(null);
  const [selectedVisitTypeId, setSelectedVisitTypeId] = useState<number | null>(null);
  const [selectedOption, setSelectedOption] = useState<SelectedAdmissionOption | null>(null);
  const [autoScheduleToday, setAutoScheduleToday] = useState(true);
  const [admissionTime, setAdmissionTime] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset step when dialog opens/closes
  useEffect(() => {
    if (open) {
      setActiveStep(0);
      const coordData = getAdmissionCoordination();
      if (coordData) {
        setCoordination(coordData);
        if (coordData.recommendedTime) {
          setAdmissionTime(coordData.recommendedTime);
        }
      }
    }
  }, [open]);

  // Initialize visit type when dialog opens
  useEffect(() => {
    if (open && admissionVisitType && !selectedVisitTypeId) {
      setSelectedVisitTypeId(admissionVisitType.id);
    }
  }, [open, admissionVisitType]);

  // Round up to nearest 5 minutes
  const roundUpToNearest5 = (date: Date): Date => {
    const ms = date.getTime();
    const fiveMinMs = 5 * 60 * 1000;
    return new Date(Math.ceil(ms / fiveMinMs) * fiveMinMs);
  };

  // Calculate recommended time once both transportation and DME are confirmed
  useEffect(() => {
    const bothConfirmed =
      manualCoordination?.transportation.confirmed && manualCoordination?.dme.confirmed;

    if (velocareStatus && manualCoordination && bothConfirmed) {
      const times: Date[] = [];

      if (velocareStatus.supplies.estimatedDelivery) {
        times.push(new Date(velocareStatus.supplies.estimatedDelivery));
      }
      if (velocareStatus.rpm.estimatedArrival) {
        times.push(new Date(velocareStatus.rpm.estimatedArrival));
      }
      if (manualCoordination.transportation.estimatedArrival) {
        times.push(new Date(manualCoordination.transportation.estimatedArrival));
      }
      if (manualCoordination.dme.estimatedDelivery) {
        times.push(new Date(manualCoordination.dme.estimatedDelivery));
      }

      if (times.length > 0) {
        const latestTime = new Date(Math.max(...times.map((t) => t.getTime())));
        const recommended = roundUpToNearest5(latestTime);

        setAdmissionTime(recommended.toISOString());

        if (coordination) {
          const updated: AdmissionCoordinationState = {
            ...coordination,
            supplies: {
              vendor: 'Velocare',
              ordered: true,
              estimatedDelivery: velocareStatus.supplies.estimatedDelivery,
              items: velocareStatus.supplies.items,
            },
            transportation: {
              ordered: manualCoordination.transportation.confirmed,
              estimatedArrival: manualCoordination.transportation.estimatedArrival,
              duration: 30,
            },
            recommendedTime: recommended.toISOString(),
          };
          saveAdmissionCoordination(updated);
          setCoordination(updated);
        }
      }
    } else {
      setAdmissionTime(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [velocareStatus, manualCoordination]);

  const handleConfirmSchedule = async () => {
    if (!admissionTime) return;

    setIsSubmitting(true);
    try {
      const providerResources = selectedOption
        ? selectedOption.resources.map((r) => ({ providerId: r.providerId }))
        : [];

      await onComplete({
        admissionTime: selectedOption?.startTime || admissionTime,
        providerResources,
        autoScheduleToday: autoScheduleToday && sameDayRemainingCount > 0,
        selectedVisitTypeId,
      });

      if (coordination) {
        saveAdmissionCoordination({ ...coordination, completed: true });
      }

      onClose();
    } catch (error) {
      console.error('Failed to schedule admission visit:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!coordination) {
    return null;
  }

  const serviceLineId = Object.values(orgConfig.serviceLines)[0]?.id;
  const carePattern = CARE_PATTERNS.standard;

  // Derive admission date from selected option (could be today or tomorrow)
  const admissionDate = selectedOption
    ? startOfDay(new Date(selectedOption.startTime))
    : startOfDay(new Date());

  // Compute remaining same-day windows (mirrors TodaysVisitScheduler logic)
  const sameDayRemainingCount = selectedOption
    ? (() => {
        const admissionHour = parseInt(
          formatInTimeZone(new Date(selectedOption.startTime), patientTimezone, 'H'),
          10
        );
        return carePattern.timeWindows.filter(w => {
          const [h] = w.earliestStart.split(':').map(Number);
          return h > admissionHour;
        }).length;
      })()
    : 0;

  const allCoordinationComplete =
    velocareStatus &&
    manualCoordination &&
    (manualCoordination.transportation.confirmed || manualCoordination.dme.confirmed);

  const canAdvance = !!(allCoordinationComplete && admissionTime && selectedOption);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: '80vh' } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5">Coordinate Care</Typography>
            <Typography variant="body2" color="text.secondary">
              {coordination.patientName} • MRN: {coordination.mrn}
            </Typography>
          </Box>
          <IconButton onClick={onClose} edge="end" sx={{ ml: 2 }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
        <Tabs value={activeStep} onChange={(_, v) => v === 1 && canAdvance ? setActiveStep(1) : v === 0 ? setActiveStep(0) : null}>
          <Tab label="Admission Coordination" />
          <Tab label="Future Visit Coordination" disabled={!canAdvance} />
        </Tabs>
      </Box>

      <DialogContent dividers>
        {activeStep === 0 ? (
          <>
            {/* Patient Info */}
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'info.lighter' }}>
              <Grid2 container spacing={2}>
                <Grid2 size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">
                    Admission Order Time
                  </Typography>
                  <Typography variant="body2">
                    {timeLabelWithZone(coordination.admissionOrderTime, {
                      ampm: false,
                      timezone: patientTimezone,
                    })}
                  </Typography>
                </Grid2>
                <Grid2 size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">
                    Diagnosis
                  </Typography>
                  <Typography variant="body2">{coordination.diagnosis}</Typography>
                </Grid2>
                <Grid2 size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">
                    Expected Length of Stay
                  </Typography>
                  <Typography variant="body2">{coordination.expectedLOS} days</Typography>
                </Grid2>
                <Grid2 size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">
                    Care Pattern
                  </Typography>
                  <Typography variant="body2">{carePattern.name}</Typography>
                </Grid2>
              </Grid2>
            </Paper>

            {/* Section 1: Coordination Status */}
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              1. Coordination Status
            </Typography>

            <Grid2 container spacing={2} sx={{ mb: 3 }}>
              <Grid2 size={{ xs: 12 }}>
                <VelocareIntegrationPanel
                  onStatusChange={setVelocareStatus}
                  disabled={false}
                  patientTimezone={patientTimezone}
                />
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                <ManualCoordinationChecklist
                  onChange={setManualCoordination}
                  disabled={false}
                  patientTimezone={patientTimezone}
                />
              </Grid2>
            </Grid2>

            <Divider sx={{ my: 3 }} />

            {/* Section 2: Best Available Visit Options */}
            <Typography variant="h6" gutterBottom>
              2. Best Available Visit Options
            </Typography>
            {admissionTime && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Recommending{' '}
                <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  {timeLabelWithZone(admissionTime, { ampm: true, timezone: patientTimezone })}
                </Box>
                {' '}today based on latest timing estimates of coordinated activities.
              </Typography>
            )}
            <Box sx={{ mb: 3 }}>
              <AdmissionVisitOptionsPanel
                patientId={coordination.patientId}
                recommendedTime={admissionTime}
                admissionVisitType={admissionVisitType}
                selectedVisitTypeId={selectedVisitTypeId}
                onVisitTypeChange={setSelectedVisitTypeId}
                serviceLineId={serviceLineId || 0}
                onOptionSelected={setSelectedOption}
                disabled={!admissionTime || orgConfig.loading}
                patientTimezone={patientTimezone}
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Section 3: Visit Schedule */}
            <Typography variant="h6" gutterBottom>
              3. {selectedOption && !isSameDay(new Date(selectedOption.startTime), new Date()) ? "Tomorrow's" : "Today's"} Visit Schedule
            </Typography>
            {selectedOption ? (
              <TodaysVisitScheduler
                admissionTime={selectedOption.startTime}
                carePatternName={carePattern.name}
                visitWindows={carePattern.timeWindows.map((window, index) => ({
                  label:
                    index === 0
                      ? 'Morning Visit'
                      : index === 1
                      ? 'Afternoon Visit'
                      : 'Evening Visit',
                  earliestStart: window.earliestStart,
                  latestStart: window.latestStart,
                }))}
                autoScheduleToday={autoScheduleToday}
                onAutoScheduleChange={setAutoScheduleToday}
                disabled={false}
                patientTimezone={patientTimezone}
              />
            ) : (
              <Alert severity="info">
                No visit options available at the recommended time. Select an option above to see today's schedule.
              </Alert>
            )}
          </>
        ) : (
          <FutureVisitCoordinationStep
            patientTimezone={patientTimezone}
            serviceLineId={serviceLineId || 0}
            selectedOption={selectedOption!}
            admissionVisitType={admissionVisitType}
            admissionDate={admissionDate}
            expectedLOS={coordination.expectedLOS}
            carePatternName={carePattern.name}
            carePatternDuration={carePattern.duration}
            carePatternTimeWindows={carePattern.timeWindows}
            autoScheduleToday={autoScheduleToday}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        {activeStep === 0 ? (
          <>
            <Button onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => setActiveStep(1)}
              disabled={!canAdvance}
            >
              Schedule Admission Visit
            </Button>
          </>
        ) : (
          <>
            <Button onClick={() => setActiveStep(0)} disabled={isSubmitting}>
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmSchedule}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Scheduling...' : 'Confirm & Schedule All Visits'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
